import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import { createGeneratorActor } from '../services/generator';
import { useAuth } from '../auth/useAuth';

interface NFTData {
  token_id: number;
  qr_code_data: string;
  rarity_tier: string;
  claimed: boolean;
}

export default function AdminPanel() {
  const [nfts, setNfts] = useState<NFTData[]>([]);
  const [selectedNFT, setSelectedNFT] = useState<NFTData | null>(null);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const { agent } = useAuth();

  useEffect(() => {
    const fetchNFTs = async () => {
      if (!agent) return;
      try {
        // Fetch all NFTs from generator canister
        const actor = await createGeneratorActor(agent);
        const allNFTs = await actor.get_all_nfts();
        
        const nftData: NFTData[] = allNFTs.map(nft => ({
          token_id: Number(nft.token_id),
          qr_code_data: nft.qr_code_data,
          rarity_tier: nft.rarity_tier,
          claimed: nft.claimed,
        }));
        
        setNfts(nftData);
      } catch (err) {
        console.error('Failed to fetch NFTs:', err);
        // Fallback to empty array
        setNfts([]);
      }
    };

    fetchNFTs();
  }, [agent]);

  const handlePrintQR = (nft: NFTData) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>IC Spicy NFT #${nft.token_id} - QR Code</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                text-align: center; 
                padding: 40px;
              }
              h1 { color: #D97706; }
              .qr-container { margin: 20px 0; }
            </style>
          </head>
          <body>
            <h1>IC Spicy RWA NFT #${nft.token_id}</h1>
            <p>Rarity: ${nft.rarity_tier}</p>
            <div class="qr-container">
              ${document.querySelector(`#qr-${nft.token_id}`)?.innerHTML || ''}
            </div>
            <p>Scan to claim your NFT</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleEmailQR = async (nft: NFTData) => {
    if (!email) {
      alert('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      // Send email with QR code via backend
      // await sendEmailWithQR(email, nft);
      alert(`QR code for NFT #${nft.token_id} sent to ${email}`);
      setEmail('');
    } catch (err) {
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const unclaimedNFTs = nfts.filter(nft => !nft.claimed);

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark rounded-2xl p-8"
        >
          <h1 className="text-4xl font-bold mb-6 text-gold">Admin Panel - QR Code Management</h1>
          
          <div className="mb-6">
            <p className="text-gray-300 mb-2">
              Total NFTs: {nfts.length} | Unclaimed: {unclaimedNFTs.length}
            </p>
          </div>

          {selectedNFT ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass rounded-2xl p-6 mb-6"
            >
              <h2 className="text-2xl font-bold mb-4 text-gold">
                NFT #{selectedNFT.token_id} - {selectedNFT.rarity_tier}
              </h2>
              
              <div className="mb-4" id={`qr-${selectedNFT.token_id}`}>
                <QRCodeSVG value={selectedNFT.qr_code_data} size={256} className="mx-auto" />
              </div>

              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => handlePrintQR(selectedNFT)}
                  className="flex-1 bg-gold hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-lg transition-all"
                >
                  Print QR Code
                </button>
                <button
                  onClick={() => setSelectedNFT(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-all"
                >
                  Back to List
                </button>
              </div>

              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Customer email"
                  className="flex-1 px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                />
                <button
                  onClick={() => handleEmailQR(selectedNFT)}
                  disabled={sending || !email}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-all"
                >
                  {sending ? 'Sending...' : 'Email QR'}
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-h-96 overflow-y-auto">
              {unclaimedNFTs.map((nft) => (
                <motion.div
                  key={nft.token_id}
                  whileHover={{ scale: 1.05 }}
                  className="glass rounded-lg p-4 cursor-pointer"
                  onClick={() => setSelectedNFT(nft)}
                >
                  <div className="text-center">
                    <p className="text-sm text-gray-400 mb-2">#{nft.token_id}</p>
                    <p className="text-xs text-gold">{nft.rarity_tier}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </BlacksmithWorkshop>
  );
}

